import { useEffect, useState } from "react";
import { useAppSelector } from "../../../hooks/redux.hook";
import { CommunityService } from "../../../_service/community.service";
import {
  DEFAULT_POST_DELETION_REASONS,
  OTHER_DELETION_CATEGORY,
} from "../../../_constants/post-deletion-reasons";

// module-level cache so the reasons list is fetched once per community
const reasonsCache = {};

/**
 * Cascading reason picker shown in the delete-post confirmation modal when an
 * admin/moderator deletes someone else's post.
 *
 * Controlled component:
 *   value: { category, detail, note }
 *   onChange: (next) => void
 *   showError: boolean  // highlight the missing required field
 */
export function DeletePostReasonFields({
  value = { category: "", detail: "", note: "" },
  onChange = () => {},
  showError = false,
  communityId: communityIdProp,
}) {
  const communityIdFromStore = useAppSelector((s) => s.community?.id);
  const communityId = communityIdProp || communityIdFromStore;

  const [reasons, setReasons] = useState(
    () => reasonsCache[communityId] || DEFAULT_POST_DELETION_REASONS
  );

  useEffect(() => {
    if (!communityId) return;
    if (reasonsCache[communityId]) {
      setReasons(reasonsCache[communityId]);
      return;
    }
    CommunityService.getPostDeletionReasons(communityId)
      .then(({ reasons: fetched }) => {
        if (Array.isArray(fetched) && fetched.length) {
          reasonsCache[communityId] = fetched;
          setReasons(fetched);
        }
      })
      .catch(() => {
        // keep defaults on failure
      });
  }, [communityId]);

  const selectedCategory = reasons.find((c) => c.category === value.category);
  const subReasons = selectedCategory ? selectedCategory.reasons : [];
  const isOther = value.category === OTHER_DELETION_CATEGORY;

  const selectClass =
    "mt-1 w-full rounded border border-border bg-card p-2 text-sm focus:outline-none";

  return (
    <div className="text-left">
      <div className="text-center text-sm">
        Please select a reason for deleting this post.
      </div>

      {/* category */}
      <div className="mt-4">
        <label className="text-xs font-semibold text-secondary-foreground/70">
          Reason category
        </label>
        <select
          className={selectClass}
          value={value.category || ""}
          onChange={(e) =>
            onChange({ category: e.target.value, detail: "", note: "" })
          }>
          <option value="">Select a category…</option>
          {reasons.map((c) => (
            <option key={c.category} value={c.category}>
              {c.category}
            </option>
          ))}
        </select>
      </div>

      {/* specific sub-reason (hidden for "Other") */}
      {value.category && !isOther ? (
        <div className="mt-3">
          <label className="text-xs font-semibold text-secondary-foreground/70">
            Specific reason
          </label>
          <select
            className={selectClass}
            value={value.detail || ""}
            onChange={(e) => onChange({ ...value, detail: e.target.value })}>
            <option value="">Select a reason…</option>
            {subReasons.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {/* note: optional for specific reasons, mandatory for "Other" */}
      {value.category ? (
        <div className="mt-3">
          <label className="text-xs font-semibold text-secondary-foreground/70">
            {isOther ? "Reason for removing this post" : "Add a note (optional)"}
          </label>
          <textarea
            className={selectClass}
            rows={3}
            placeholder={
              isOther
                ? "Please provide the reason for removing this post"
                : "Optional note for the record"
            }
            value={value.note || ""}
            onChange={(e) => onChange({ ...value, note: e.target.value })}
          />
        </div>
      ) : null}

      {showError ? (
        <div className="mt-1 text-xs text-alert">
          {!value.category
            ? "Please choose a reason category"
            : isOther
              ? "Please provide the reason for removing this post"
              : "Please choose a specific reason"}
        </div>
      ) : null}
    </div>
  );
}

export default DeletePostReasonFields;
